/**
 * FEATURE FLAGS - Sistema de configuración para habilitar/deshabilitar funcionalidades
 * 
 * Uso:
 * - En desarrollo: Los flags se obtienen de variables de entorno o valores por defecto
 * - En producción: Se desactivan automáticamente a menos que se habiliten explícitamente
 * - Para pruebas en producción: Usar localStorage para activar flags específicos
 */

// Detectar si estamos en producción
const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = process.env.NODE_ENV === 'development';

// Valores por defecto según el ambiente
const defaultFlags = {
  // Logs de consola
  enableConsoleLogs: isDevelopment,
  enableApiLogs: isDevelopment,
  enableErrorLogs: true, // Siempre habilitado
  enableWarningLogs: true, // Siempre habilitado
  enableDebugLogs: isDevelopment,
  
  // Alerts de debug
  enableDebugAlerts: isDevelopment,
  enableSuccessAlerts: isDevelopment,
  enableErrorAlerts: true, // Siempre habilitado
  
  // Funcionalidades específicas
  enableVoiceRecognitionLogs: isDevelopment,
  enableOrderLogs: isDevelopment,
  enableProductLogs: isDevelopment,
  enableEnvivoLogs: isDevelopment,
  enableVersionCheckLogs: isDevelopment,
  
  // Performance y métricas
  enablePerformanceLogs: isDevelopment,
  enableNetworkLogs: isDevelopment,

  // Mantenimiento global de la aplicación
  // En producción queda activo por defecto hasta que backend o override local lo desactiven.
  enableMaintenanceMode: isProduction,
};

// Función para obtener el valor de un flag desde localStorage
const getLocalStorageFlag = (flagName) => {
  try {
    const value = localStorage.getItem(`featureFlag_${flagName}`);
    if (value !== null) {
      return value === 'true';
    }
  } catch (error) {
    console.error('Error leyendo feature flag de localStorage:', error);
  }
  return null;
};

// Función para establecer un flag en localStorage
export const setFeatureFlag = (flagName, value) => {
  try {
    localStorage.setItem(`featureFlag_${flagName}`, String(value));
    console.log(`✅ Feature flag "${flagName}" establecido a: ${value}`);
    window.location.reload(); // Recargar para aplicar cambios
  } catch (error) {
    console.error('Error guardando feature flag:', error);
  }
};

// Función para limpiar todos los flags de localStorage
export const clearFeatureFlags = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('featureFlag_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('✅ Todos los feature flags limpiados');
    window.location.reload();
  } catch (error) {
    console.error('Error limpiando feature flags:', error);
  }
};

// Función para habilitar modo debug en producción
export const enableProductionDebug = () => {
  setFeatureFlag('enableConsoleLogs', true);
  setFeatureFlag('enableApiLogs', true);
  setFeatureFlag('enableDebugLogs', true);
  setFeatureFlag('enableDebugAlerts', true);
  setFeatureFlag('enableVoiceRecognitionLogs', true);
  setFeatureFlag('enableOrderLogs', true);
  setFeatureFlag('enableProductLogs', true);
  setFeatureFlag('enableEnvivoLogs', true);
};

// Función para deshabilitar modo debug en producción
export const disableProductionDebug = () => {
  clearFeatureFlags();
};

// Crear objeto de feature flags con valores finales
const createFeatureFlags = () => {
  const flags = {};
  
  Object.keys(defaultFlags).forEach(flagName => {
    // Primero intentar obtener de localStorage
    const localValue = getLocalStorageFlag(flagName);
    
    if (localValue !== null) {
      // Si hay un valor en localStorage, usarlo
      flags[flagName] = localValue;
    } else {
      // Si no, usar el valor por defecto
      flags[flagName] = defaultFlags[flagName];
    }
  });
  
  return flags;
};

// Exportar los feature flags
export const featureFlags = createFeatureFlags();

// Funciones auxiliares para logging condicional
export const debugLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableDebugLogs) {
    console.log('[DEBUG]', ...args);
  }
};

export const apiLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableApiLogs) {
    console.log('[API]', ...args);
  }
};

export const errorLog = (...args) => {
  if (featureFlags.enableErrorLogs) {
    console.error('[ERROR]', ...args);
  }
};

export const warningLog = (...args) => {
  if (featureFlags.enableWarningLogs) {
    console.warn('[WARNING]', ...args);
  }
};

export const voiceLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableVoiceRecognitionLogs) {
    console.log('[VOICE]', ...args);
  }
};

export const orderLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableOrderLogs) {
    console.log('[ORDER]', ...args);
  }
};

export const productLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableProductLogs) {
    console.log('[PRODUCT]', ...args);
  }
};

export const envivoLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enableEnvivoLogs) {
    console.log('[ENVIVO]', ...args);
  }
};

export const performanceLog = (...args) => {
  if (featureFlags.enableConsoleLogs && featureFlags.enablePerformanceLogs) {
    console.log('[PERFORMANCE]', ...args);
  }
};

// Función para alerts condicionales
export const debugAlert = (message, type = 'info') => {
  if (featureFlags.enableDebugAlerts) {
    switch (type) {
      case 'success':
        if (featureFlags.enableSuccessAlerts) {
          alert(`✅ ${message}`);
        }
        break;
      case 'error':
        if (featureFlags.enableErrorAlerts) {
          alert(`❌ ${message}`);
        }
        break;
      default:
        alert(`ℹ️ ${message}`);
    }
  }
};

// Mostrar configuración actual en consola (solo en desarrollo o si está habilitado)
if (featureFlags.enableConsoleLogs) {
  console.log('🚩 Feature Flags activos:', featureFlags);
  console.log('🌍 Ambiente:', isProduction ? 'PRODUCCIÓN' : 'DESARROLLO');
  
  if (isProduction) {
    console.log('💡 Para habilitar debug en producción, ejecuta en consola:');
    console.log('   window.enableProductionDebug()');
    console.log('💡 Para deshabilitar debug en producción:');
    console.log('   window.disableProductionDebug()');
  }
}

// Exponer funciones globalmente para acceso desde consola
if (typeof window !== 'undefined') {
  window.enableProductionDebug = enableProductionDebug;
  window.disableProductionDebug = disableProductionDebug;
  window.setFeatureFlag = setFeatureFlag;
  window.clearFeatureFlags = clearFeatureFlags;
  window.showFeatureFlags = () => {
    console.table(featureFlags);
  };
}

export default featureFlags;
